// Copyright (c) Docugami, Inc. All rights reserved.

import { CommandBarButton, ICommandBarItemProps, Icon, IconButton, mergeStyles, Shimmer, TooltipHost } from "@fluentui/react";
import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { ExampleChunksModal } from "components/common/ExampleChunksModal";
import { FeedbackConfirmationButton } from "components/common/FeedbackConfirmationButton";
import { dgFontFamily } from "components/common/Fonts";
import { appContentColor, disabledDarkGreyText, greyText, primaryTheme, themeRedDark } from "components/shell/presentation/AppStyles";
import { validateBinding } from "hooks/ChunkLinking";
import { useWaitCursor } from "hooks/WaitCursor";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useDocumentDgml } from "hooks/documentDgml";
import { useDropWithTrigger } from "hooks/dropWithTrigger";
import { useExampleChunksForLabel } from "hooks/exampleChunksForLabel";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useReportEntryActions } from "hooks/reportEntryActions";
import { spanIdObjectReadingOrderComparerThunk, useReportEntryItemSlots } from "hooks/reportEntrySlots";
import { useAddBinding } from "hooks/reports";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { createOperationId, setActiveAddEntryGroupRequest, useRemoveEntriesMutation } from "store/reports/reportsSlice";
import { selectClusterId, selectIsReportPublished, selectOrderedReportEntryIds, selectReportDocumentId, selectReportDocumentsView, selectReportEntryBindingsForCurrentDocument, selectReportEntryBindingsLoading } from "store/reports/selectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { CardHeader } from "./CardHeader";
import { ActiveCardletDropTargetStyle, ActiveEditingCardletStyle, DisabledCardletStyle, DragWrapper, ItemCardletStyles, SelectedCardletStyle } from "./CardStyles";
import { CommandButton, ConfirmationCommandButton, IconAlignment } from "./CommandButton";
import { DndItem, DndReportItem, DndType, isDndChunk, isDndReportItem } from "./DndTypes";
import { ItemCardlet } from "./ItemCardlet";
import { ISlotItem, isReportChunkSlot, ItemSlot, LabelChunkSlotItem, ReportChunkSlotItem, SlotItemType } from "./ItemSlot";
import { ItemSlots } from "./ItemSlots";
import { PlaceholderSlotVisual } from "./PlaceholderSlotVisual";
import { IReportEntryItem, IReportItem, ReportItemFooter } from "./ReportItemCardlet";
import { ReportEntryItemTitleInputField } from "./ReportItemTitleInputField";
import { DocumentEvaluationStatus } from "networking/reportsApi";
import { useRepeatsExpansion } from "hooks/repeatsExpansion";
import { LabelQualityControlDialog } from "components/reports/LabelQualityControlDialog";

const ContentRoot = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    ${props => !!props.disabled && DisabledCss};
`

interface EditLabelProps {
    spanIds: string[];
    isEditing: boolean;
    hasEdits: boolean;
    disabled: boolean;
    onClickSpan: (spanId: string) => void;
    onRemoveSpan: (spanId: string) => void;
    confirm: (additionalSpanIds?: string[]) => void;
    edit: () => void;
    cancel: () => void;
    onComplete: () => void;
}

interface ReportEntryItemCardletProps {
    item: IReportEntryItem;
    onClick?: (slotItem?: ISlotItem, childItem?: IReportItem) => void;
    dragDisabled?: boolean;
    collapsed?: boolean;
    editLabelProps: EditLabelProps;
}

export const ReportEntryItemCardlet = (props: ReportEntryItemCardletProps): JSX.Element => {
    const [hovered, setHovered] = useState(false);
    const [exampleModalOpen, setExampleModalOpen] = useState(false);
    const [showExpandResults, setShowExpandResults] = useState(false);
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [negativeExpandSpanIds, setNegativeExpandSpanIds] = useState<string[]>([]);

    const rootRef = useRef<HTMLDivElement | null>(null);
    const itemSlotRef = useRef<HTMLDivElement | null>(null);
    const dgml = useDocumentDgml();

    const { entry, isDisabled, isSelected } = props.item;

    const { spanIds: editSpanIds, isEditing, hasEdits, disabled: editDisabled, onClickSpan, onRemoveSpan, confirm: confirmEdit, edit: startEdit, cancel: cancelEdit } = props.editLabelProps;

    const { trigger: triggerAddBinding } = useAddBinding(entry.reportId);
    const [triggerRemove, removeState] = useRemoveEntriesMutation();
    const { exampleChunks } = useExampleChunksForLabel(entry.title, false);

    const dispatch = useAppDispatch();

    useWaitCursor(removeState.isLoading);

    const onRemove = useCallback(() => {
        if (!entry) {
            return;
        }
        triggerRemove({ reportId: entry.reportId, entryIds: [entry.id] })
    }, [entry, triggerRemove])
    const { canRemoveEntry: canRemove } = useReportEntryActions();

    const folderId = useAppSelector(selectReportDocumentId)!;
    const docsetId = useAppSelector(selectClusterId)!;
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);
    const isPublished = useAppSelector(selectIsReportPublished);
    const documentStatus = useAppSelector(state => selectReportDocumentsView(state)?.entities[folderId]?.state.evaluationStatus);
    const isDocumentPublished = isPublished && documentStatus !== DocumentEvaluationStatus.None;
    const isEntryLoadingBindings = useAppSelector(state => selectReportEntryBindingsLoading(state, dgml.fileId) || state.reportsSlice.loadingEntries?.some(x => x.fileId === dgml.fileId && x.title === entry.title));

    const header = <ReportEntryItemTitleInputField entry={entry} reportId={props.item.reportId} readonly={isPublished} />;
    const footer = <ReportItemFooter item={props.item} />;

    const { slots, isEmpty } = useReportEntryItemSlots({ item: props.item });
    const count = isEmpty ? 0 : slots.length;
    const visibleSlots = !!props.collapsed && count > 0 ? [slots[0]] : slots;

    useEffect(() => {
        if (!isEditing) {
            setNegativeExpandSpanIds([]);
            setShowExpandResults(false);
        }
    }, [isEditing]);

    const expandResults = useRepeatsExpansion(dgml.fileId, editSpanIds, negativeExpandSpanIds, showExpandResults && isEditing);
    const editingSlots = useMemo((): LabelChunkSlotItem[] => {
        function createSlot(spanId?: string, isSuggestion?: boolean): LabelChunkSlotItem {
            const item: LabelChunkSlotItem = !!spanId ? {
                id: spanId,
                type: SlotItemType.LabelChunk,
                spanId: spanId,
                label: entry.title,
                isSuggestion: isSuggestion
            } : {
                id: 'empty-slot',
                type: SlotItemType.LabelChunk,
                spanId: '',
                label: entry.title,
                isSuggestion: isSuggestion
            };

            return item;
        }

        let slots = !editSpanIds.length ? [createSlot()] : editSpanIds.map(x => createSlot(x));
        if (!!expandResults?.length && showExpandResults) {
            slots.push(...expandResults.map(chunk => createSlot(chunk.spanId, true)));
        }

        slots = dispatch(spanIdObjectReadingOrderComparerThunk(slots));
        return slots;
    }, [editSpanIds, entry.title, dispatch, showExpandResults, expandResults]);

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndReportItem, any, { isDragging: boolean }>({
        type: DndType.ReportItem,
        item: {
            type: DndType.ReportItem,
            entryId: entry.id
        },
        canDrag: !props.dragDisabled,
        collect: m => ({
            isDragging: m.isDragging()
        })
    }, [entry]);

    const { triggerRef, dropTargetRef, isVisible, isOver } = useDropWithTrigger({
        dropTypes: DndType.Chunk,
        onDrop: async (item) => {
            if (isDndChunk(item)) {
                await triggerAddBinding({
                    folderId: folderId,
                    entryId: entry.id,
                    instanceId: item.instance.spanId
                });
            }
        },
        canDrop: item => !isEmpty && !props.item.isDisabled && isDndChunk(item) && dgml.isInitialized() && validateBinding(item.instance, documentBindings?.entities[entry.id]?.bindings)
    })

    const canDropForGroup = useCallback((dragItem: DndItem) =>
        !props.item.isDisabled && isDndReportItem(dragItem) && dragItem.entryId !== props.item.entry.id && !props.item.entry.groupId, [props.item])

    const [isTargetActive, targetRef] = useDrop<DndItem, any, boolean>({
        accept: DndType.ReportItem,
        canDrop: canDropForGroup,
        drop: async (item, m) => {
            if (isDndReportItem(item) && m.canDrop()) {
                // Give order precedence to the current, drop-target entry in the add group request
                const orderedEntries = !!entryIds ? [...entryIds] : undefined;
                if (!!orderedEntries) {
                    const draggedEntryIdx = orderedEntries.findIndex(x => x === item.entryId);
                    const draggedEntry = orderedEntries.splice(draggedEntryIdx, 1)[0];
                    const targetIdx = orderedEntries.findIndex(x => x === props.item.entry.id);
                    orderedEntries.splice(Math.min(targetIdx + 1, orderedEntries.length), 0, draggedEntry);
                }

                dispatch(setActiveAddEntryGroupRequest({
                    reportId: props.item.reportId,
                    label: "",
                    entries: [props.item.entry.id, item.entryId],
                    orderedEntries: orderedEntries,
                    operationId: createOperationId()
                }))
            }
        },
        collect: m => !!m.isOver() && !!m.getItem() && m.canDrop()
    });

    const onDrop = useCallback(async (item: ISlotItem, dndItem: DndItem) => {
        if (!isReportChunkSlot(item) || !isDndChunk(dndItem)) {
            return;
        }

        const instanceId = dndItem.instance.spanId!;
        await triggerAddBinding({
            entryId: props.item.entry.id,
            folderId: folderId,
            instanceId: instanceId,
            prevInstanceId: item.binding?.spanId
        })
    }, [triggerAddBinding, folderId, props.item.entry.id]);

    const bindings = documentBindings?.entities[entry.id]?.bindings;
    const canDrop = useCallback((item: ISlotItem, dropItem: DndItem): boolean => {
        return isReportChunkSlot(item) &&
            isDndChunk(dropItem) &&
            dgml.isInitialized() &&
            validateBinding(dropItem.instance, bindings);
    }, [dgml, bindings]);

    async function onClickPlaceholder() {
        props.onClick?.({
            id: '',
            type: SlotItemType.ReportChunk,
            entryId: entry.id
        } as ReportChunkSlotItem)
    }

    dragVisualRef(rootRef);
    triggerRef(rootRef);
    dropTargetRef(itemSlotRef);

    targetRef(rootRef);

    const cardletStyles: ItemCardletStyles | undefined = useMemo(() => {
        if (isEditing) {
            return {
                root: [ActiveEditingCardletStyle]
            }
        }
        if (!!isSelected) {
            return {
                root: [SelectedCardletStyle]
            }
        }
        if (isDisabled) {
            return {
                root: [DisabledCardletStyle]
            }
        }
        if (!!isTargetActive) {
            return {
                root: [ActiveCardletDropTargetStyle]
            }
        }
    }, [isDisabled, isTargetActive, isSelected, isEditing])

    const isRepeatPlaceholderVisible = useMemo(() => {
        const addRepeatValid = !!bindings && bindings.filter(x => !!x.spanId).length > 0;
        return addRepeatValid && (isVisible || (props.item.isLinkable && hovered));
    }, [isVisible, props.item.isLinkable, hovered, bindings])

    const overflowCommands: ICommandBarItemProps[] = useMemo(() => [
        getRemoveLabelButtonProps(!!canRemove, onRemove),
        {
            key: 'settings',
            name: 'Settings',
            iconOnly: false,
            iconProps: { iconName: '' },
            onRenderIcon: () => < Icon title="Settings" iconName={dgIconNames[dgIconNames.DgSettings]} className={getIconClassName(false)} />,
            onClick: () => setShowSettingsDialog(true),
        }
    ], [onRemove, canRemove]);

    const commands: ICommandBarItemProps[] = useMemo(() => isDocumentPublished ? [] : [
        {
            key: 'complete',
            name: 'Complete',
            iconOnly: true,
            iconProps: { iconName: '' },
            onRenderIcon: () => (
                <FeedbackConfirmationButton label={entry.title} docsetId={docsetId} documentId={folderId} labelId={entry.labelId} />
            )
        }
    ], [entry, docsetId, folderId, isDocumentPublished]);

    const getContent = () => {
        if (isEntryLoadingBindings) {
            return (
                <ItemSlot hideManagementCommands={true}>
                    <Shimmer width={250} />
                </ItemSlot>
            )
        }
        else if (isEditing) {
            return <ItemSlots
                slots={editingSlots}
                onClick={item => onClickSpan((item as LabelChunkSlotItem).spanId)}
                onRemove={item => {
                    const slotItem = item as LabelChunkSlotItem;
                    if (!!slotItem.isSuggestion) {
                        setNegativeExpandSpanIds(x => [...x, slotItem.spanId]);
                    }

                    onRemoveSpan(slotItem.spanId)
                }}
                scrollOnSelection={true} />
        }
        else {
            return <ItemSlots slots={visibleSlots} onDrop={onDrop} canDrop={canDrop} onClick={props.onClick} scrollOnSelection={!!props.item.isSelected} />
        }
    }

    function onSave() {
        confirmEdit(showExpandResults && !!expandResults?.length ? expandResults.map(x => x.spanId) : undefined);
    }

    return (
        <DragWrapper
            isDragging={isDragging}
            onClick={() => props.onClick?.()}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}>
            <ItemCardlet
                ref={rootRef}
                automationId={AutomationId.WorkPathDocumentItemCardlet}
                dragHandleRef={!props.dragDisabled ? dragRef : undefined}
                header={header}
                footer={footer}
                canRemove={canRemove}
                onRemove={onRemove}
                headerCommands={commands}
                styles={cardletStyles}
                onExamplesClick={!!exampleChunks?.length ? () => setExampleModalOpen(true) : undefined}
                overflowCommands={overflowCommands}>
                <ContentRoot>
                    <CardHeader count={isEditing ? (!editSpanIds.length ? 0 : editingSlots.length) : count} showSingle={!props.collapsed}>
                        {isEditing && !!expandResults?.length &&
                            <TooltipHost content={showExpandResults ? 'Hide positional suggestions' : 'Show positional suggestions'}>
                                <IconButton
                                    iconProps={{ iconName: showExpandResults ? 'Hide2' : 'View' }}
                                    styles={{ root: { marginLeft: 10, width: 20, height: 20 }, icon: { color: appContentColor, fontSize: '10px' } }}
                                    onClick={() => setShowExpandResults(x => !x)} />
                            </TooltipHost>
                        }
                        <EditButton hidden={!!props.collapsed} editDisabled={editDisabled} isEditing={isEditing} hasEdits={hasEdits} onEdit={startEdit} onSave={onSave} onCancel={cancelEdit} />
                    </CardHeader>
                    {getContent()}
                    <div ref={itemSlotRef}>
                        <PlaceholderSlotVisual
                            text="Drop an instance here or click here to add a repeating instance of this item."
                            isVisible={isRepeatPlaceholderVisible}
                            isActive={isOver}
                            onClick={onClickPlaceholder} />
                    </div>
                </ContentRoot>
            </ItemCardlet>
            <ExampleChunksModal isOpen={exampleModalOpen} onDismiss={() => setExampleModalOpen(false)} label={entry.title} autoDismissWhenEmpty={true} />
            {showSettingsDialog && <LabelQualityControlDialog
                docsetId={docsetId}
                labelId={props.item.entry.labelId}
                onClose={() => setShowSettingsDialog(false)} />}
        </DragWrapper>
    );
}

const EditButtonRoot = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: auto;
`;

interface EditButtonProps {
    hidden?: boolean;
    editDisabled?: boolean;
    isEditing: boolean;
    hasEdits: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
}

const EditButton = (props: EditButtonProps) => {
    const { icon } = useDgIcons();

    if (!!props.hidden) {
        return null;
    }

    if (props.isEditing) {
        return (
            <EditButtonRoot>
                <CommandBarButton
                    title='Save Edits'
                    text="SAVE"
                    styles={{
                        root: { padding: "5px", fontFamily: dgFontFamily, color: !props.editDisabled ? appContentColor : disabledDarkGreyText, fontSize: "12px" },
                        label: { fontWeight: 500 }
                    }}
                    iconProps={{ iconName: "" }}
                    onRenderIcon={() => icon(dgIconNames.DgSave, 'Save label edits', 16, 14.4, !props.editDisabled ? appContentColor : disabledDarkGreyText, '')}
                    disabled={!!props.editDisabled}
                    onClick={props.onSave} />
                <CommandBarButton
                    title='Cancel Edits'
                    text="Cancel"
                    styles={{
                        root: { padding: "5px", fontFamily: dgFontFamily, color: !props.editDisabled ? appContentColor : disabledDarkGreyText, fontSize: "10px" },
                        label: { fontWeight: 500 }
                    }}
                    onRenderText={() => {
                        const command = {
                            label: 'Cancel',
                            showLabel: true,
                            canExecute: true,
                            labelColor: appContentColor,
                            onClick: props.onCancel
                        };

                        const confirmationCommand = {
                            ...command,
                            confirmationLabel: 'Discard edits?',
                            confirmationColor: primaryTheme.palette.white,
                            confirmationBackgroundColor: themeRedDark
                        }

                        return <CommandButton command={props.hasEdits ? confirmationCommand : command} />
                    }} />
            </EditButtonRoot>
        )
    }

    return (
        <EditButtonRoot>
            <CommandBarButton
                title='Edit Instances'
                text="EDIT INSTANCES"
                styles={{
                    root: { padding: "5px", fontFamily: dgFontFamily, color: !props.editDisabled ? appContentColor : disabledDarkGreyText, fontSize: "12px" },
                    label: { fontWeight: 500 }
                }}
                iconProps={{ iconName: "" }}
                onRenderIcon={() => icon(dgIconNames.DgEditLabel, 'Edit instances', 15, 15, !props.editDisabled ? appContentColor : disabledDarkGreyText, '')}
                disabled={!!props.editDisabled}
                onClick={props.onEdit} />
        </EditButtonRoot>
    )
}

const getIconClassName = (disabled?: boolean) => mergeStyles({
    height: 30,
    width: 14,
    selectors: {
        '.primaryFill': {
            fill: !!disabled ? disabledDarkGreyText : appContentColor
        },
    }
})

const getRemoveLabelButtonProps = (canRemove: boolean, onRemove?: () => void, iconOnly?: boolean): ICommandBarItemProps => ({
    key: 'remove_item_button',
    name: 'Remove',
    iconOnly: iconOnly,
    iconProps: { iconName: '' },
    onRenderIcon: () => {
        if (!iconOnly) {
            return < Icon title="Remove" iconName={dgIconNames[dgIconNames.DgTrashCan]} className={getIconClassName(!canRemove)} />
        }
        else {
            return (
                <ConfirmationCommandButton
                    command={{
                        icon: {
                            name: dgIconNames.DgTrashCan,
                            width: 26,
                            height: 30,
                            defaultColor: greyText
                        },
                        label: 'remove',
                        canExecute: canRemove,
                        onClick: onRemove,
                        confirmationLabel: 'Remove?',
                        confirmationColor: primaryTheme.palette.white,
                        confirmationBackgroundColor: themeRedDark
                    }}
                    width={14}
                    height={14}
                    iconAlignment={IconAlignment.Left}
                    dataAutomationId={AutomationId.RemoveEntryConfirmationButton} />
            )
        }
    },
    disabled: !canRemove,
    onClick: !iconOnly ? onRemove : undefined,
})
